/*
 * PF Scroll Pilot guided option steps (Shoplift test: templates/product.guided-options.json).
 *
 * Apple-style buy flow: after each choice the page glides to the next step the shopper has not
 * done yet this visit. The steps, in visual order: the option groups (Color, Storage,
 * Condition), then the add-ons, protection and Boost rows, then the Add to cart band.
 * - Options: a tap on a value, including the one already selected.
 * - Add-ons: accessories are multi-select, so adding one never moves the page. The row's
 *   "No thanks" / "Continue" button (guided template only) does.
 * - Protection: a tap on a plan, a term or "No accident protection".
 * - Boost: claiming the offer or "No thanks". Removing it does not move the page.
 *
 * - Every option starts unpicked for shoppers who land without ?variant= (see "Options start
 *   unpicked" below), so everyone makes each choice and walks the steps.
 *
 * Only runs when the adaptive options block has guided_option_steps on (data-pf-guided-steps), so
 * the control template keeps the old "re-center the row you just picked" behavior.
 * pf-scroll-pilot-behavior.js calls onPick() from the option pick handler and consume() from the
 * post-variant-change scroll stash.
 */
(() => {
  "use strict";

  // The pilot section markup (with its script tags) is re-inserted on combined-listing swaps. The
  // first copy keeps the state, so a re-run must not replace it.
  if (window.PfScrollGuidedSteps) return;

  const SECTION = '[data-pf-experience="pf_scroll_pilot"]';
  const ROW = ".pf-product-scroll__row";
  const OPTION_ROW = "[data-pf-option-row]";
  // The details block in the Add to cart band: title, chosen options, price, Add to cart. Not the
  // band itself, which opens with a large product photo on a phone and would leave Add to cart
  // below the fold.
  const BUY_BAND = ".pf-scroll-product-info__shell";
  const CONTINUE = "[data-pf-guided-continue]";
  const ACCESSORY = "[data-pf-scroll-complementary-option]";
  const ARRIVAL_CLASS = "is-pf-guided-arrival";
  // A change only counts as a tap when a pointer went down in the same row this recently. Arrow
  // keys also change a radio group, and a keyboard shopper must not be pulled away mid-group.
  const POINTER_PICK_WINDOW_MS = 1500;
  // A short beat after the tap so the new selection registers before the page moves.
  const GLIDE_DELAY_MS = 180;
  // Rows shorter than this are empty shells (an add-on row with nothing to offer, say).
  const MIN_ROW_HEIGHT = 24;
  // After the page re-renders, re-aim only if the target drifted by more than this.
  const DRIFT_TOLERANCE_PX = 32;
  // Re-aim checks after the re-render: as soon as the new rows are in, and once more after the
  // pilot's staggered re-clones (up to 600ms) and late images have settled.
  const REAIM_DELAYS_MS = [0, 700];
  // How long after a glide lands to check it once more.
  const LANDED_CHECK_MS = 400;

  // Steps the shopper has done this visit, by key: an option name, "add_ons", "protection", "boost".
  const touchedOptions = new Set();
  // The stash holds a JSON copy of the step; keep the live one so an early glide is not reported
  // twice.
  let liveStep = null;
  let lastPointer = { row: null, at: -Infinity };
  let lastUserScrollAt = -Infinity;

  const now = () => (window.performance?.now ? window.performance.now() : Date.now());

  const optionKey = (row) =>
    String(row?.getAttribute?.("data-option-name") || "")
      .trim()
      .toLowerCase();

  // Combined-listing swaps and the pilot's context rebuild replace the section element, so never
  // hold on to one: find the section that is on screen now.
  const findLiveSection = () =>
    Array.from(document.querySelectorAll(SECTION)).find(
      (section) => section.isConnected && section.getClientRects().length > 0
    ) || null;

  const isEnabled = (section) =>
    Boolean(section?.querySelector?.("[data-pf-adaptive-options][data-pf-guided-steps]"));

  const isVisibleRow = (row) => {
    if (row.hidden || row.closest?.("[hidden]")) return false;
    if (!row.getClientRects().length) return false;
    return row.getBoundingClientRect().height >= MIN_ROW_HEIGHT;
  };

  // Visual order, not DOM order: below 960px the enhancements CSS moves protection and Boost to the
  // end of the column with `order`.
  const visibleRows = (section) =>
    Array.from(section.querySelectorAll(ROW))
      .filter(isVisibleRow)
      .map((row) => ({ row, top: row.getBoundingClientRect().top }))
      .sort((a, b) => a.top - b.top)
      .map(({ row }) => row);

  const isOptionRow = (row) => row.matches(OPTION_ROW);

  // A sold-out value never moves the guide on: there is nothing to buy there yet. The radios in the
  // adaptive rows carry no aria-disabled and are never truly disabled; sold out shows only as the
  // "disabled" class (or "visually-disabled" on swatches) and data-pf-target-available, the same
  // checks as isUnavailableOption in pf-scroll-pilot-enhancements.js.
  const isUnavailable = (input) =>
    Boolean(
      input &&
        (input.disabled ||
          input.getAttribute?.("aria-disabled") === "true" ||
          input.classList?.contains("disabled") ||
          input.classList?.contains("visually-disabled") ||
          input.dataset?.pfTargetAvailable === "false")
    );

  const describeRow = (row) => {
    if (isOptionRow(row)) return optionKey(row);
    if (row.matches("[data-pf-warranty-row]")) return "protection";
    if (row.matches("[data-pf-boost-row]")) return "boost";
    if (row.querySelector("[data-pf-scroll-complementary-selector]")) return "add_ons";
    return "section";
  };

  // Rows that are steps. Other rows in the column (a video or info row, say) are passed over.
  const isStepRow = (row) => describeRow(row) !== "section";

  const findBuyBand = (section) => {
    const band = section.querySelector(BUY_BAND);
    return band && band.getClientRects().length ? band : null;
  };

  /**
   * Where to go after a choice in the row keyed `fromKey`.
   * - The next step below it that the shopper has not done yet.
   * - Otherwise, on the first choice in this row, the Add to cart band.
   * - Otherwise (a change of mind with every later step done) nowhere: stay put.
   * Re-resolved on every glide: combined-listing swaps rebuild the rows, the add-ons load late, and
   * the protection row only shows once SureBright has quotes.
   */
  const resolveNextStep = (rows, fromKey, wasTouched) => {
    const steps = rows.filter(isStepRow);
    const index = steps.findIndex((row) => describeRow(row) === fromKey);
    if (index < 0) return null;
    const next = steps.slice(index + 1).find((row) => !touchedOptions.has(describeRow(row)));
    if (next) return { key: describeRow(next), row: next };
    if (wasTouched) return null;
    return { key: "buy_buttons", row: null };
  };

  const findStepTarget = (section, step) => {
    const next = resolveNextStep(visibleRows(section), step.fromOption, step.wasTouched);
    if (!next) return null;
    return next.row ? { key: next.key, el: next.row } : { key: next.key, el: findBuyBand(section) };
  };

  const headerOffset = () => {
    const header = document.querySelector(".section-header");
    const bottom = header?.getBoundingClientRect?.().bottom || 0;
    const offset = bottom > 0 && bottom < 200 ? bottom : 60;
    // On desktop the guided bar is a strip under the header: land below it.
    const strip = document.querySelector(".pf-scroll-sticky-atc:not([hidden])");
    const rect = strip?.getBoundingClientRect?.();
    return rect && rect.height && rect.top < 200 && rect.bottom > offset ? rect.bottom : offset;
  };

  const prefersReducedMotion = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

  const targetTopFor = (el) => el.getBoundingClientRect().top - headerOffset() - 16;

  const emitGuidedStep = (section, step, targetKey) => {
    const detail = {
      from_option: step.fromOption,
      to_target: targetKey,
      product_id: String(
        section.querySelector("[data-pf-adaptive-options]")?.dataset.pfProductId || ""
      ),
      product_handle: window.location.pathname.split("/products/")[1]?.split("/")[0] || "",
      source: "pf_scroll",
      pdp_experience: "pf_scroll_pilot",
      view: new URL(window.location.href).searchParams.get("view") || "",
      // "change" for a new value, "reselect" for a tap on the value that was already selected,
      // "choice" for a protection or Boost choice, "continue" for the add-ons button, "gate" for
      // an Add to cart tap that went to Condition first.
      trigger: step.trigger,
      // false when the page opened with Condition unpicked, true when a ?variant= link chose it.
      condition_preselected: conditionPreselected(),
      // The same for every option (Color, Storage, Condition...).
      options_preselected: optionsPreselected(),
    };
    // Add to cart taps held back: the options still to pick.
    if (step.unpicked) detail.unpicked = step.unpicked.join(",");
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "pdp_guided_step", ...detail });
    try {
      window.posthog?.capture?.("pdp_guided_step", detail);
    } catch {
      // analytics must never break the picker
    }
  };

  // Fade the row in only when it arrives from below the fold. A row already on screen would visibly
  // flicker.
  const markArrival = (row, distance) => {
    if (prefersReducedMotion() || distance < window.innerHeight * 0.5) return;
    row.classList.remove(ARRIVAL_CLASS);
    // Restart the animation if the same row is reached twice in a row.
    void row.offsetWidth; // eslint-disable-line no-void
    row.classList.add(ARRIVAL_CLASS);
    window.setTimeout(() => row.classList.remove(ARRIVAL_CLASS), 900);
  };

  const shopperScrolledSince = (at) => lastUserScrollAt > at;

  // The glide steps the page itself, frame by frame, instead of a native smooth scroll. A native
  // one carries on to the target when the shopper scrolls mid-glide (desktop), and stopping it with
  // an instant scrollTo also cancels the shopper's own wheel scroll. This one just stops stepping.
  const GLIDE_MIN_MS = 350;
  const GLIDE_MAX_MS = 700;
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
  let activeGlide = null;
  const animateScroll = (targetY) => {
    const startY = window.scrollY;
    const travel = targetY - startY;
    const duration = Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, Math.abs(travel) * 0.6));
    const startedAt = now();
    const run = {};
    activeGlide = run;
    const frame = () => {
      if (activeGlide !== run) return;
      const progress = Math.min(1, (now() - startedAt) / duration);
      window.scrollTo({ top: startY + travel * easeInOut(progress), behavior: "instant" });
      if (progress < 1) window.requestAnimationFrame(frame);
      else activeGlide = null;
    };
    window.requestAnimationFrame(frame);
    return run;
  };
  const stopGlide = () => {
    activeGlide = null;
  };

  // First call: glide to the target row. Later calls (after the re-render) re-aim only when the
  // page is off target: a combined-listing swap can jump the page on its own. A glide still in
  // flight towards an unchanged target is left alone. Corrections after the glide has landed snap,
  // because a second long glide back would read as a bounce.
  const glide = (step) => {
    const section = findLiveSection();
    if (!section || shopperScrolledSince(step.pickedAt)) return false;
    // A newer choice supersedes this one (two quick taps): only the latest step moves the page.
    if (liveStep && liveStep.pickedAt > step.pickedAt) return false;
    const target = findStepTarget(section, step);
    const row = target?.el;
    if (!row) return false;
    const distance = targetTopFor(row);
    const targetY = Math.max(0, window.scrollY + distance);
    const isCorrection = typeof step.targetY === "number";
    const inFlight = isCorrection && Boolean(step.run) && activeGlide === step.run;
    const targetMoved = isCorrection && Math.abs(targetY - step.targetY) > DRIFT_TOLERANCE_PX;
    if (inFlight && !targetMoved) return true;
    step.targetY = targetY;
    if (Math.abs(distance) <= DRIFT_TOLERANCE_PX) return true;
    // The fade-up is styled for rows only, not the Add to cart band.
    if (!isCorrection && target.key !== "buy_buttons") markArrival(row, distance);
    const smooth = !prefersReducedMotion() && (!isCorrection || inFlight);
    if (smooth) {
      step.run = animateScroll(targetY);
    } else {
      stopGlide();
      window.scrollTo({ top: targetY, behavior: "instant" });
    }
    if (!step.reported) {
      step.reported = true;
      emitGuidedStep(section, step, target.key);
    }
    return true;
  };

  /**
   * Called by the pilot pick handler. Returns the step to stash for after the re-render, or null to
   * keep the old behavior.
   */
  const onPick = ({ row: handlerRow }) => {
    const key = optionKey(handlerRow);
    if (!key) return null;

    // Trust the row the finger touched: after a combined-listing swap the pick handler can still
    // hold a row from the page that was swapped out. Only taps count as picks: after a swap the
    // pilot moves Condition to an in-stock value through the same handler, and that must not mark
    // Condition as chosen.
    const pickedAt = now();
    const row = lastPointer.row;
    const tapped =
      row?.isConnected &&
      optionKey(row) === key &&
      pickedAt - lastPointer.at <= POINTER_PICK_WINDOW_MS;
    if (!tapped) return null;
    // A new sold-out value keeps the old behavior (re-center the row) and does not count as picked.
    if (isUnavailable(row.querySelector?.('input[type="radio"]:checked'))) return null;
    markOptionPicked(row);
    return beginStep(row, pickedAt, "change");
  };

  // Records the row as done and starts the glide to the next step. Returns the step, or null when
  // the page should stay put.
  const beginStep = (row, pickedAt, trigger) => {
    const section = row.closest(SECTION);
    if (!section || !isEnabled(section)) return null;
    const key = describeRow(row);
    if (!key || key === "section") return null;
    const wasTouched = touchedOptions.has(key);
    touchedOptions.add(key);
    queueSyncUnpicked();

    const next = resolveNextStep(visibleRows(section), key, wasTouched);
    if (!next) return null;
    // `target` is where the glide is headed now; glide() re-resolves it as the page changes.
    const step = {
      target: next.key,
      fromOption: key,
      wasTouched,
      pickedAt,
      trigger,
      reported: false,
    };
    liveStep = step;

    // Glide right away instead of waiting for the variant request (a combined-listing pick can take
    // 3s to swap the page). The swapped-in sibling has the same rows in the same places, and
    // consume() re-aims afterwards if anything moved.
    window.setTimeout(() => glide(step), GLIDE_DELAY_MS);
    // One more look once the glide has landed: a page still loading (or a protection list that
    // re-rendered on the pick) can shift the target after the fact. glide() only snaps back when
    // the target moved and the shopper has not scrolled since.
    window.setTimeout(() => glide(step), GLIDE_DELAY_MS + GLIDE_MAX_MS + LANDED_CHECK_MS);
    return step;
  };

  // A tap on the value that is already selected (the variant the page preselected, say) changes
  // nothing, so no change event reaches the pick handler. It still means "this one", so it moves the
  // shopper on too. The radio's state is read at pointerdown: by the time the click fires, a tap on
  // a new value has already checked it.
  let pendingReselect = null;
  const radioFor = (target) => {
    const label = target?.closest?.("label");
    if (label) {
      return (
        label.control || (label.htmlFor ? document.getElementById(label.htmlFor) : null) || null
      );
    }
    return target?.closest?.('input[type="radio"]') || null;
  };

  /**
   * Called after the variant re-render with the stashed step (a plain object from sessionStorage).
   * Scrolls, or re-aims if the page shifted during an early glide. Always returns true so the pilot
   * does not also re-center the row that was just picked.
   */
  const consume = (stashedStep) => {
    const step = liveStep?.pickedAt === stashedStep?.pickedAt ? liveStep : stashedStep;
    if (!step) return true;
    window.requestAnimationFrame(() => {
      REAIM_DELAYS_MS.forEach((delay) => {
        window.setTimeout(() => glide(step), delay);
      });
    });
    return true;
  };

  document.addEventListener(
    "pointerdown",
    (event) => {
      const row = event.target?.closest?.(`${SECTION} ${ROW}`);
      lastPointer = { row: row || null, at: now() };
      const input = row?.matches(OPTION_ROW) ? radioFor(event.target) : null;
      pendingReselect =
        input?.checked && !isUnavailable(input) ? { input, row, at: lastPointer.at } : null;
    },
    { capture: true, passive: true }
  );

  // A label tap fires two clicks (the label's, then the radio's); the first one consumes the
  // pending re-select.
  document.addEventListener("click", (event) => {
    const pending = pendingReselect;
    pendingReselect = null;
    if (!pending || now() - pending.at > POINTER_PICK_WINDOW_MS) return;
    const input = radioFor(event.target);
    if (input !== pending.input || !input.checked || !pending.row.isConnected) return;
    markOptionPicked(pending.row);
    beginStep(pending.row, now(), "reselect");
  });

  // Choices in the rows under the options are buttons, not radios. Capture phase: the pilot
  // re-renders the protection list on a pick, which would detach the target before a bubbling
  // listener saw it, and the Boost toggle's state must be read before the pilot flips it.
  const sectionChoice = (target) => {
    const row = target?.closest?.(`${SECTION} ${ROW}`);
    if (!row) return null;
    const key = describeRow(row);
    if (key === "add_ons" && target.closest(CONTINUE)) return { row, trigger: "continue" };
    if (key === "protection" && target.closest("[data-pf-scroll-warranty-choice-value]")) {
      return { row, trigger: "choice" };
    }
    if (key === "boost") {
      if (target.closest("[data-pf-boost-offer-decline]")) return { row, trigger: "choice" };
      const claim = target.closest(".pf-pilot-boost-offer__toggle");
      // Claiming moves on; a tap that un-claims does not.
      if (claim && claim.getAttribute("aria-pressed") !== "true") return { row, trigger: "choice" };
    }
    return null;
  };
  document.addEventListener(
    "click",
    (event) => {
      if (event.isTrusted === false) return;
      const choice = sectionChoice(event.target);
      if (!choice) return;
      // Like the options, only taps count (keyboard shoppers are not pulled away), except the
      // Continue button, whose only job is to move on.
      const tapped =
        lastPointer.row === choice.row && now() - lastPointer.at <= POINTER_PICK_WINDOW_MS;
      if (!tapped && choice.trigger !== "continue") return;
      beginStep(choice.row, now(), choice.trigger);
    },
    { capture: true }
  );

  // "No thanks" until an accessory is added, then "Continue". The pilot sets aria-pressed on the
  // accessory buttons, including when it re-applies the selection after a re-render.
  const syncContinueLabels = () => {
    document.querySelectorAll(`${SECTION} ${CONTINUE}`).forEach((button) => {
      const row = button.closest(ROW);
      const added = row?.querySelector(`${ACCESSORY}[aria-pressed="true"]`);
      const label = added ? button.dataset.labelAdded : button.dataset.labelNone;
      if (label && button.textContent.trim() !== label) button.textContent = label;
    });
  };
  if (window.MutationObserver && document.body) {
    new MutationObserver(syncContinueLabels).observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-pressed"],
    });
  }

  // The shopper scrolling mid-glide wins: the glide stops where it is and later re-aims are skipped.
  // Taps (touchstart, pointerdown) are left out so a quick double tap keeps its glide.
  const noteUserScroll = () => {
    lastUserScrollAt = now();
    stopGlide();
  };
  const SCROLL_KEYS = new Set(["PageUp", "PageDown", "Home", "End", "ArrowUp", "ArrowDown", " "]);
  window.addEventListener("wheel", noteUserScroll, { passive: true });
  window.addEventListener("touchmove", noteUserScroll, { passive: true });
  window.addEventListener("keydown", (event) => {
    if (SCROLL_KEYS.has(event.key)) noteUserScroll();
  });

  /*
   * Options start unpicked. A shopper who lands without ?variant= (a collection card, search) sees
   * no Color, Storage or Condition chosen and picks each one on the way down. _pf-pilot-adaptive.liquid
   * marks the block with data-pf-guided-start-unpicked and lists the options (2+ values each) in
   * data-pf-guided-options. Links that name a variant (Shopping ads, emails) keep them preselected.
   *
   * The page still holds a real variant underneath: on combined listings each Color/Storage pair is
   * its own product, so the photo and title show the one the shopper landed on, and prices,
   * protection quotes and Boost keep working. Until every option is picked:
   * - the prices read "From $X": the lowest in-stock price on this product (or the held variant's
   *   once the product's own options are picked) and one swap away on each unpicked Color/Storage;
   * - Add to cart (and the sticky bar) reads the first unpicked row's "Choose a ..." label, and a
   *   tap glides to that row instead of adding;
   * - unpicked rows show no value as chosen (data-pf-guided-blank on the row).
   * Picks can come in any order. The state lives in this script (it outlives section swaps) and on
   * <html> (data-pf-guided-unpicked); pf-scroll-pilot.css does the showing and hiding.
   */
  const UNPICKED_ATTR = "data-pf-guided-unpicked";
  const BLANK_ROW_ATTR = "data-pf-guided-blank";
  const FROM_PRICE = "pf-guided-from-price";
  const CHOOSE_LABEL = "pf-guided-choose-label";
  const GUIDED_BLOCK = "[data-pf-adaptive-options][data-pf-guided-steps]";
  // null until the first guided section is read; then the option keys still to pick.
  let unpickedOptions = null;
  // What the first guided section said: options it listed, and whether they started unpicked.
  let startedOptions = null;
  let startedUnpicked = false;

  const readGuidedOptions = (block) => {
    try {
      const data = JSON.parse(block?.getAttribute?.("data-pf-guided-options") || "null");
      return data && typeof data.options === "object" ? data : null;
    } catch {
      return null;
    }
  };

  // Words the guide writes into the page, translated in _pf-pilot-adaptive.liquid.
  const DEFAULT_STRINGS = {
    from: "From {price}",
    save: "Save up to {percent}%",
    summary: "Your {model}.",
    protection: "Accident protection",
    noProtection: "None",
    boost: "Boost Mobile, 3 months",
    boostIncluded: "$0*",
    total: "Total",
  };
  const readStrings = (block) => {
    try {
      return {
        ...DEFAULT_STRINGS,
        ...JSON.parse(block?.getAttribute?.("data-pf-guided-strings") || "{}"),
      };
    } catch {
      return DEFAULT_STRINGS;
    }
  };
  const fill = (template, values) =>
    Object.entries(values).reduce(
      (text, [key, value]) => text.replace(`{${key}}`, value),
      template
    );

  // "condición" on the Spanish storefront.
  const isConditionKey = (key) => key.includes("condition") || key.includes("condici");

  // null when the product has no Condition option (or nothing in stock); else whether the page
  // opened with it chosen.
  const conditionPreselected = () => {
    if (!startedOptions) return null;
    if (!startedOptions.some(isConditionKey)) return null;
    return !startedUnpicked;
  };
  const optionsPreselected = () => (startedOptions ? !startedUnpicked : null);

  const isUnpickedRow = (row) =>
    Boolean(unpickedOptions?.size) &&
    Boolean(row?.matches?.(OPTION_ROW)) &&
    unpickedOptions.has(optionKey(row));

  // The first unpicked option row on screen, in visual order.
  const findFirstUnpickedRow = (section) =>
    visibleRows(section).find((row) => isUnpickedRow(row)) || null;

  const rootElement = () => document.documentElement;

  const setUnpickedFlag = (on) => {
    const root = rootElement();
    if (!root) return;
    if (on) root.setAttribute(UNPICKED_ATTR, "");
    else root.removeAttribute(UNPICKED_ATTR);
  };

  // Our own children, appended last: the pilot's button code reads the first <span> as the label.
  const ensureFromPrice = (parent, text, className) => {
    let el = parent.querySelector(`:scope > .${FROM_PRICE}`);
    if (!el) {
      el = document.createElement(className === "price" ? "div" : "span");
      el.className = `${className} ${FROM_PRICE}`;
      if (className === "price") {
        el.classList.add("price--large");
        const item = document.createElement("span");
        item.className = "price-item price-item--regular";
        el.appendChild(item);
      }
      // The top price's Afterpay slot (in the HTML) goes under the From price, not above it.
      const afterpay = parent.querySelector(":scope > .pf-guided-afterpay");
      if (afterpay) afterpay.before(el);
      else parent.appendChild(el);
    }
    const target = el.querySelector(".price-item") || el;
    if (target.textContent !== text) target.textContent = text;
  };

  const ensureChooseLabel = (button, text) => {
    let el = button.querySelector(`:scope > .${CHOOSE_LABEL}`);
    if (!el) {
      el = document.createElement("span");
      el.className = CHOOSE_LABEL;
      button.appendChild(el);
    }
    if (el.textContent !== text) el.textContent = text;
  };

  // [cents, "$X"] pairs from the block: this product's own options use `self` while any of them is
  // unpicked (else `selected`), and each unpicked option whose values are other products adds its
  // lowest price one swap away.
  const fromPriceFor = (data) => {
    const isPair = (pair) => Array.isArray(pair) && typeof pair[0] === "number" && pair[1];
    const ownUnpicked = Object.keys(data.options).some(
      (key) => unpickedOptions.has(key) && !data.options[key]?.from
    );
    const candidates = [ownUnpicked ? data.self : data.selected];
    unpickedOptions.forEach((key) => candidates.push(data.options[key]?.from));
    return candidates
      .filter(isPair)
      .reduce((low, pair) => (!low || pair[0] < low[0] ? pair : low), null)?.[1];
  };

  const markBlankRows = (section, on) => {
    Array.from(section.querySelectorAll(OPTION_ROW)).forEach((row) => {
      if (on && isUnpickedRow(row)) {
        if (!row.hasAttribute?.(BLANK_ROW_ATTR)) row.setAttribute?.(BLANK_ROW_ATTR, "");
      } else if (row.hasAttribute?.(BLANK_ROW_ATTR)) {
        row.removeAttribute?.(BLANK_ROW_ATTR);
      }
    });
  };

  // Re-applied after every re-render: combined-listing swaps and variant renders replace the rows,
  // the price and the buttons, and the sticky bar is built late.
  const syncUnpicked = () => {
    const section = findLiveSection();
    const block = section?.querySelector?.(GUIDED_BLOCK);
    if (!block) return;
    const data = readGuidedOptions(block);
    if (unpickedOptions === null) {
      startedOptions = data ? Object.keys(data.options) : null;
      startedUnpicked = Boolean(data) && block.hasAttribute("data-pf-guided-start-unpicked");
      unpickedOptions = new Set(startedUnpicked ? startedOptions : []);
    }
    // A sibling with nothing in stock has no options list: it shows the normal sold-out state.
    const firstRow = data ? findFirstUnpickedRow(section) : null;
    const label = firstRow ? data.options[optionKey(firstRow)]?.label : null;
    const fromPrice = data && unpickedOptions.size ? fromPriceFor(data) : null;
    const on = Boolean(label && fromPrice);
    const strings = readStrings(block);
    rootElement()?.setAttribute?.(GUIDED_ATTR, "");
    setUnpickedFlag(on);
    markBlankRows(section, on);
    syncExtras(section, data, strings, on);
    if (!on) return;
    const fromText = fill(strings.from, { price: fromPrice });
    section
      .querySelectorAll(".pf-scroll-product-info__price")
      .forEach((price) => ensureFromPrice(price, fromText, "price"));
    const introPrice = section.querySelector(INTRO_PRICE);
    if (introPrice && data.save > 0) {
      ensureSaveBadge(introPrice, fill(strings.save, { percent: data.save }));
    }
    section
      .querySelectorAll(".product-form__submit")
      .forEach((button) => ensureChooseLabel(button, label));
    document
      .querySelectorAll(".pf-scroll-sticky-atc__meta")
      .forEach((meta) => ensureFromPrice(meta, fromText, "pf-scroll-sticky-atc__price"));
    document
      .querySelectorAll(".pf-scroll-sticky-atc__submit")
      .forEach((button) => ensureChooseLabel(button, label));
  };

  /*
   * The rest of the Apple-style walk, re-applied on every sync:
   * - while options are blank, the top title reads the model ("iPhone 16") with an "Unlocked ·
   *   Certified Pre-Owned" line, the From price gets "Save up to X%", and an Afterpay line sits
   *   under it (the From amount, then the real price);
   * - a picked row's heading reads "Color: Black" (data-pf-guided-picked);
   * - steps after the current one are faded (data-pf-guided-upcoming) but still tappable;
   * - the sticky bar carries a photo, the model and the picks so far;
   * - the buy band at the end reads "Your iPhone 16." and lists protection, Boost and the total.
   */
  const GUIDED_ATTR = "data-pf-guided";
  const PICKED_ROW_ATTR = "data-pf-guided-picked";
  const UPCOMING_ROW_ATTR = "data-pf-guided-upcoming";
  const INTRO_PRICE = ".pf-scroll-product-intro .pf-scroll-product-info__price";
  const SAVE_BADGE = "pf-guided-save-badge";

  const setAttr = (el, name, on) => {
    if (!el?.setAttribute) return;
    if (on && !el.hasAttribute(name)) el.setAttribute(name, "");
    else if (!on && el.hasAttribute(name)) el.removeAttribute(name);
  };
  const setText = (el, text) => {
    if (el && el.textContent !== text) el.textContent = text;
  };
  const checkedValue = (row) => {
    const checked = Array.from(row.querySelectorAll?.('input[type="radio"]:checked') || []).find(
      (input) => input.closest?.(OPTION_ROW) === row
    );
    return checked?.value || "";
  };

  const ensureSaveBadge = (introPrice, text) => {
    const from = introPrice.querySelector(`:scope > .${FROM_PRICE}`);
    if (!from) return;
    let badge = from.querySelector(`.${SAVE_BADGE}`);
    if (!badge) {
      badge = document.createElement("span");
      badge.className = `badge price__badge-discount ${SAVE_BADGE}`;
      from.appendChild(badge);
    }
    setText(badge, text);
  };

  // The intro title: the model name while options are blank, the full title (kept in the h1 for
  // search engines) the rest of the time.
  const syncTitle = (section, data) => {
    const heading = section.querySelector(".pf-scroll-product-intro h1");
    if (!heading || !data?.model) return;
    if (!heading.querySelector(".pf-guided-title__full")) {
      const full = heading.textContent.trim();
      heading.textContent = "";
      const model = document.createElement("span");
      model.className = "pf-guided-title__model";
      model.setAttribute("aria-hidden", "true");
      const fullSpan = document.createElement("span");
      fullSpan.className = "pf-guided-title__full";
      fullSpan.textContent = full;
      heading.append(model, fullSpan);
    }
    setText(heading.querySelector(".pf-guided-title__model"), data.model);
    let sub = heading.parentElement?.querySelector(":scope > .pf-guided-title__sub");
    if (!sub && heading.parentElement) {
      sub = document.createElement("p");
      sub.className = "pf-guided-title__sub";
      heading.after(sub);
    }
    setText(sub, data.sub || "");
  };

  // Afterpay under the top price while options are blank: a copy of the buy band's placement with
  // the From amount (the price block's own Afterpay is hidden with the real price until the last
  // pick). After that the CSS hides the copy and the price block's own line shows.
  const syncAfterpay = (section, data, on) => {
    const introPrice = section.querySelector(INTRO_PRICE);
    const source = Array.from(section.querySelectorAll("square-placement")).find(
      (el) => !el.closest(".pf-scroll-product-intro")
    );
    if (!introPrice || !source) return;
    // The slot comes with the HTML (pf-pilot-product-price.liquid) so the page doesn't move.
    let copy = introPrice.querySelector(":scope > .pf-guided-afterpay");
    if (!copy) {
      copy = document.createElement("div");
      copy.className = "pf-guided-afterpay";
      introPrice.appendChild(copy);
    }
    if (!copy.querySelector("square-placement")) {
      // No id: the buy band's placement keeps it, and two elements can't share one.
      const clone = source.cloneNode(false);
      clone.removeAttribute("id");
      copy.appendChild(clone);
    }
    const placement = copy.querySelector("square-placement");
    const cents = on ? fromCentsFor(data) : null;
    const amount = cents != null ? (cents / 100).toFixed(2) : source.getAttribute("data-amount");
    if (amount && placement && placement.getAttribute("data-amount") !== amount) {
      placement.setAttribute("data-amount", amount);
    }
  };

  // Headings: "Color. Pick the one you like." until a pick, then "Color: Black".
  const syncHeadings = (section) => {
    Array.from(section.querySelectorAll(OPTION_ROW)).forEach((row) => {
      const value = checkedValue(row);
      const picked = Boolean(value) && !unpickedOptions?.has(optionKey(row));
      setAttr(row, PICKED_ROW_ATTR, picked);
      if (picked) setText(row.querySelector("[data-pf-guided-picked-value]"), value);
    });
  };

  // The current step is the first option still blank. Steps after it fade. Once every option is
  // picked nothing fades, so add-ons, protection and Boost look the same as on the normal page
  // (a ?variant= link, or a shopper who scrolls past "No thanks", never sees protection faded).
  const syncUpcoming = (section, data) => {
    const steps = data ? visibleRows(section).filter(isStepRow) : [];
    const current = steps.findIndex(
      (row) => isOptionRow(row) && unpickedOptions?.has(optionKey(row))
    );
    Array.from(section.querySelectorAll(ROW)).forEach((row) => {
      const index = steps.indexOf(row);
      setAttr(row, UPCOMING_ROW_ATTR, current >= 0 && index > current);
    });
  };

  const pickedValues = (section) =>
    visibleRows(section)
      .filter((row) => isOptionRow(row) && !unpickedOptions?.has(optionKey(row)))
      .map(checkedValue)
      .filter(Boolean);

  // The product's full title, as the intro h1 shows it.
  const introTitleText = (section) => {
    const heading = section.querySelector(".pf-scroll-product-intro h1");
    return (
      heading?.querySelector(".pf-guided-title__full")?.textContent ||
      heading?.textContent ||
      ""
    ).trim();
  };

  const thumbSrc = (section) => {
    const img = section.querySelector(".pf-scroll-product-info__media-img");
    const src = img?.getAttribute("src") || "";
    if (!src) return "";
    return /[?&]width=\d+/.test(src)
      ? src.replace(/([?&]width=)\d+/, "$1160")
      : `${src}${src.includes("?") ? "&" : "?"}width=160`;
  };

  // The sticky bar (pf-scroll-pilot-enhancements.js builds it): a photo of the current variant, the
  // model name and the picks so far.
  const syncBar = (section, data) => {
    const bar = document.querySelector(".pf-scroll-sticky-atc");
    const meta = bar?.querySelector(".pf-scroll-sticky-atc__meta");
    if (!bar || !meta) return;
    let thumb = bar.querySelector(":scope > .pf-guided-bar__thumb");
    const src = thumbSrc(section);
    if (!thumb && src) {
      thumb = document.createElement("img");
      thumb.className = "pf-guided-bar__thumb";
      thumb.alt = "";
      thumb.width = 40;
      thumb.height = 40;
      bar.prepend(thumb);
    }
    if (thumb && src && thumb.getAttribute("src") !== src) thumb.setAttribute("src", src);
    let title = meta.querySelector(":scope > .pf-guided-bar__title");
    if (!title) {
      title = document.createElement("span");
      title.className = "pf-guided-bar__title";
      meta.prepend(title);
    }
    setText(title, data?.model || introTitleText(section));
    let picks = meta.querySelector(":scope > .pf-guided-bar__picks");
    if (!picks) {
      picks = document.createElement("span");
      picks.className = "pf-guided-bar__picks";
      title.after(picks);
    }
    setText(picks, pickedValues(section).join(" · "));
  };

  const moneyFormat = () => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: window.Shopify?.currency?.active || "USD",
      });
    } catch {
      return null;
    }
  };
  const parseCents = (text) => {
    const match = String(text || "").match(/(\d[\d,]*)(?:\.(\d{2}))?/);
    if (!match) return null;
    return Number(match[1].replace(/,/g, "")) * 100 + Number(match[2] || 0);
  };
  const fromCentsFor = (data) => {
    const text = data ? fromPriceFor(data) : null;
    return text ? parseCents(text) : null;
  };

  const ensureSummaryLine = (list, key, label, value) => {
    let line = list.querySelector(`[data-pf-guided-line="${key}"]`);
    if (!value) {
      line?.remove();
      return;
    }
    if (!line) {
      line = document.createElement("div");
      line.className = "pf-guided-summary__line";
      line.setAttribute("data-pf-guided-line", key);
      line.innerHTML =
        '<span class="pf-guided-summary__label"></span><span class="pf-guided-summary__value"></span>';
      list.appendChild(line);
    }
    setText(line.querySelector(".pf-guided-summary__label"), label);
    setText(line.querySelector(".pf-guided-summary__value"), value);
  };

  const SUMMARY_LINE_ORDER = ["protection", "boost", "total"];

  const buyButtonsOf = (band) => band.querySelector(".pf-scroll-product-info__buy-buttons");

  // The buy band at the end: "Your iPhone 16." and a short order summary above Add to cart.
  const syncSummary = (section, data, strings, on) => {
    const band = section.querySelector("[data-pf-scroll-product-info]");
    if (!band) return;
    const model = data?.model || introTitleText(section) || "";
    // "Need a moment? Save for later" goes right under Add to cart and the delivery date.
    const save = band.querySelector(".pf-guided-save");
    const delivery = band.querySelector(".pf-delivery-estimate, [class*='delivery-estimate']");
    const anchor = delivery?.closest(".shopify-block") || delivery || buyButtonsOf(band);
    if (save && anchor && anchor.nextElementSibling !== save) anchor.after(save);

    const mediaTitle = band.querySelector(".pf-scroll-product-info__media-title");
    if (mediaTitle && model) setText(mediaTitle, fill(strings.summary, { model }));

    const buyButtons = band.querySelector(".pf-scroll-product-info__buy-buttons");
    if (!buyButtons?.parentElement) return;
    let list = band.querySelector(".pf-guided-summary");
    if (!list) {
      list = document.createElement("div");
      list.className = "pf-guided-summary";
      buyButtons.parentElement.insertBefore(list, buyButtons);
    }
    const visible = (el) => el && el.getClientRects().length > 0;

    const plan = Array.from(
      section.querySelectorAll("[data-pf-scroll-warranty-choice-value].is-selected")
    ).find(visible);
    let protectionText = "";
    let protectionCents = 0;
    if (plan) {
      if (plan.getAttribute("data-pf-scroll-warranty-choice-value") === "no_coverage") {
        protectionText = strings.noProtection;
      } else {
        // "1 Year of Accident Protection" reads "1 Year" next to the "Accident protection" label.
        const name = plan.innerText
          .split("\n")
          .map((line) => line.trim())
          .find((line) => /protection/i.test(line))
          ?.replace(/\s+of\s+accident\s+protection/i, "");
        protectionCents = parseCents(plan.innerText.match(/\$\s?\d[\d,]*(\.\d{2})?/)?.[0]) || 0;
        const format = moneyFormat();
        protectionText = [
          name,
          protectionCents && format ? format.format(protectionCents / 100) : "",
        ]
          .filter(Boolean)
          .join(" · ");
      }
    }
    ensureSummaryLine(list, "protection", strings.protection, protectionText);

    const boostClaimed = Array.from(
      section.querySelectorAll('.pf-pilot-boost-offer__toggle[aria-pressed="true"]')
    ).some(visible);
    ensureSummaryLine(list, "boost", strings.boost, boostClaimed ? strings.boostIncluded : "");

    // The total, once every option is picked: the device, the added accessories and protection.
    const priceEl = Array.from(
      band.querySelectorAll(
        ".pf-scroll-product-info__price .price-item--sale, .pf-scroll-product-info__price .price-item--regular"
      )
    ).find((el) => visible(el) && !el.closest(`.${FROM_PRICE}`) && !el.closest("s"));
    const deviceCents = on ? null : parseCents(priceEl?.textContent);
    const addOnCents = Array.from(
      section.querySelectorAll('[data-pf-scroll-complementary-option][aria-pressed="true"]')
    )
      .filter((el) => !el.closest(".pf-pilot-boost-offer"))
      .reduce((sum, el) => sum + (Number(el.getAttribute("data-variant-price")) || 0), 0);
    const format = moneyFormat();
    const total =
      deviceCents != null && format
        ? format.format((deviceCents + addOnCents + protectionCents) / 100)
        : "";
    ensureSummaryLine(list, "total", strings.total, total);
    // Lines are added as their data arrives (a ?variant= link has its total before the protection
    // quotes load), so put them back in order, with the total last. Only moves them when needed.
    const lines = Array.from(list.children);
    const ordered = SUMMARY_LINE_ORDER.map((key) =>
      lines.find((line) => line.getAttribute("data-pf-guided-line") === key)
    ).filter(Boolean);
    if (ordered.some((line, index) => lines[index] !== line)) {
      ordered.forEach((line) => list.appendChild(line));
    }
    setAttr(list, "data-pf-guided-summary-empty", !list.children.length);
  };

  // The desktop strip sits under the header, which slides away on scroll.
  const syncHeaderOffset = () => {
    const header = document.querySelector(".section-header");
    const bottom = Math.max(0, Math.round(header?.getBoundingClientRect?.().bottom || 0));
    rootElement()?.style?.setProperty?.("--pf-guided-header-bottom", `${bottom}px`);
  };

  // Full prices on every option, sold-out ones included. Two other scripts own .pf-pill-price and
  // write a "+$50.00" difference into it (pf-scroll-enhancements.js rewrites it whenever anything
  // else touches it), so the guide leaves that span alone: it adds its own full price next to it,
  // written only when the amount changes, and pf-scroll-pilot.css hides theirs on this page.
  const GUIDED_PILL_PRICE = "pf-guided-pill-price";
  const syncPillPrices = (section) => {
    const format = moneyFormat();
    if (!format) return;
    section.querySelectorAll(`${OPTION_ROW} .pf-pill-price`).forEach((price) => {
      const input = price.closest("label")?.previousElementSibling;
      const cents = Number(
        input?.getAttribute?.("data-pf-target-price") || price.dataset?.pfOptionTargetPriceCents
      );
      const next = price.nextElementSibling;
      let mine = next?.classList?.contains(GUIDED_PILL_PRICE) ? next : null;
      if (!Number.isFinite(cents) || cents <= 0) {
        mine?.remove();
        return;
      }
      if (!mine) {
        mine = document.createElement("span");
        mine.className = GUIDED_PILL_PRICE;
        price.after(mine);
      }
      setText(mine, format.format(cents / 100));
      const soldOut = price.classList.contains("pf-pill-price--soldout");
      if (mine.classList.contains("is-sold-out") !== soldOut) {
        mine.classList.toggle("is-sold-out", soldOut);
      }
    });
  };

  const syncExtras = (section, data, strings, on) => {
    syncHeadings(section);
    syncPillPrices(section);
    syncUpcoming(section, data);
    if (on) syncTitle(section, data);
    syncAfterpay(section, data, on);
    syncBar(section, data);
    syncSummary(section, data, strings, on);
    syncHeaderOffset();
  };

  let unpickedObserver = null;
  let syncQueued = false;
  const queueSyncUnpicked = () => {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncUnpicked();
    });
  };
  window.addEventListener("scroll", () => window.requestAnimationFrame(syncHeaderOffset), {
    passive: true,
  });

  // The shopper chose a value in this row (a tap, or a keyboard change or press).
  const markOptionPicked = (row) => {
    if (!isUnpickedRow(row)) return;
    unpickedOptions.delete(optionKey(row));
    syncUnpicked();
  };

  // The live pilot product form, if an option still needs a pick.
  const gatedForm = () => {
    if (!unpickedOptions?.size || !rootElement()?.hasAttribute(UNPICKED_ATTR)) return null;
    return findLiveSection()?.querySelector?.("[data-pf-scroll-pilot-form] form") || null;
  };

  // Add to cart with an option unpicked: glide to the first unpicked row instead of adding.
  const gateToFirstUnpicked = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const section = findLiveSection();
    const row = section && findFirstUnpickedRow(section);
    if (!row) return;
    const pickedAt = now();
    // Supersede any glide still pending from an earlier choice.
    liveStep = { pickedAt };
    const distance = targetTopFor(row);
    if (Math.abs(distance) > DRIFT_TOLERANCE_PX) {
      markArrival(row, Math.abs(distance));
      const targetY = Math.max(0, window.scrollY + distance);
      if (prefersReducedMotion()) {
        stopGlide();
        window.scrollTo({ top: targetY, behavior: "instant" });
      } else {
        animateScroll(targetY);
      }
    }
    const radio =
      row.querySelector('input[type="radio"]:checked') || row.querySelector('input[type="radio"]');
    radio?.focus?.({ preventScroll: true });
    emitGuidedStep(
      section,
      { fromOption: "add_to_cart", trigger: "gate", unpicked: Array.from(unpickedOptions) },
      optionKey(row)
    );
  };

  // Capture phase, ahead of the pilot's form handler and any app listening on the button. Covers
  // the Add to cart button and the sticky bar (a button tied to the form by its form attribute).
  document.addEventListener(
    "click",
    (event) => {
      const form = gatedForm();
      if (!form) return;
      const button = event.target?.closest?.('button, input[type="submit"]');
      if (!button || button.type !== "submit" || button.form !== form) return;
      gateToFirstUnpicked(event);
    },
    { capture: true }
  );
  document.addEventListener(
    "submit",
    (event) => {
      const form = gatedForm();
      if (form && event.target === form) gateToFirstUnpicked(event);
    },
    { capture: true }
  );

  // Keyboard shoppers pick too: arrow keys change the radio (a trusted change), and Space or Enter
  // on the checked one confirms it. The pilot's own in-stock correction is not trusted, so it
  // never counts.
  const optionRowOf = (input) =>
    input?.type === "radio" ? input.closest?.(`${SECTION} ${OPTION_ROW}`) || null : null;
  document.addEventListener(
    "change",
    (event) => {
      if (!unpickedOptions?.size || event.isTrusted === false) return;
      const row = optionRowOf(event.target);
      if (row && !isUnavailable(event.target)) markOptionPicked(row);
    },
    { capture: true }
  );
  document.addEventListener(
    "keydown",
    (event) => {
      if (!unpickedOptions?.size || (event.key !== " " && event.key !== "Enter")) return;
      const row = optionRowOf(event.target);
      if (row && event.target.checked && !isUnavailable(event.target)) markOptionPicked(row);
    },
    { capture: true }
  );

  syncUnpicked();
  // Watch re-renders for as long as the page is open: swaps replace the rows, the protection list
  // re-renders on a pick, and add-ons, protection and Boost choices flip aria-pressed/aria-checked.
  if (window.MutationObserver && document.body) {
    // pf-scroll-enhancements.js rewrites the "+$" pill prices many times a second (on the normal
    // page too); those rewrites change nothing the guide shows, so they never queue a sync.
    const isPillPriceChurn = (record) => {
      const el = record.target?.nodeType === 3 ? record.target.parentElement : record.target;
      return Boolean(el?.closest?.(".pf-pill-price"));
    };
    unpickedObserver = new MutationObserver((records) => {
      if (records.every(isPillPriceChurn)) return;
      queueSyncUnpicked();
    });
    unpickedObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-pressed", "aria-checked"],
    });
  }

  window.PfScrollGuidedSteps = {
    onPick,
    consume,
    conditionPreselected,
    optionsPreselected,
    // Exposed for tests/pilot/pf-pilot-guided-steps.test.mjs.
    test: {
      resolveNextStep,
      describeRow,
      touchedOptions,
      syncContinueLabels,
      syncUnpicked,
      isConditionPending: () => Array.from(unpickedOptions || []).some(isConditionKey),
      unpickedOptions: () => Array.from(unpickedOptions || []),
    },
  };
})();
